import { useEffect, useRef } from 'react';

/**
 * Dev-only guard for un-virtualized lists (audit #65).
 *
 * Several large tables (negative-keyword search terms, keyword manager,
 * optimizations) render every row with no virtualization, so they degrade on
 * big accounts. Until those tables are virtualized, this hook emits a single
 * `console.warn` in development when a list crosses `threshold` rows, so the
 * perf cliff is visible instead of silent. It never changes rendering and is a
 * no-op in production builds.
 *
 * @param rowCount - number of rows about to be rendered
 * @param listName - label for the warning (e.g. "NegativeKeywordsManager search terms")
 * @param threshold - row count above which to warn (default 200)
 */
export function useLargeListWarning(rowCount: number, listName: string, threshold = 200): void {
  const warnedRef = useRef(false);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (rowCount > threshold && !warnedRef.current) {
      warnedRef.current = true;
      console.warn(
        `[perf] "${listName}" is rendering ${rowCount} un-virtualized rows ` +
          `(threshold ${threshold}). This list should be virtualized — see audit #65.`,
      );
    }
    if (rowCount <= threshold) {
      // Reset so a later spike re-warns.
      warnedRef.current = false;
    }
  }, [rowCount, listName, threshold]);
}
